/**
 * useOwnOrganisations (#3371) — the own-group organisation list.
 *
 * App-local rather than a member of ``hooks/useTwin.ts`` for one structural
 * reason: it is built on :func:`ownOrganisationsQueryBody`, which lives beside
 * the section that owns it. A hook in ``hooks/`` importing from ``pages/`` would
 * point the dependency the wrong way.
 *
 * **Its own query key, under the twin namespace.** Distinct from
 * ``twinKeys.entities.list`` so this section's invalidation and its optimistic
 * overlay stay scoped to it — but still prefixed ``['twin', …]``, so the blanket
 * ``twinKeys.all`` invalidation that ``useEntityEdit`` and the ``twin:*`` SSE
 * stream fire still refreshes it. That blanket invalidation is what eventually
 * converges the optimistic overrides with the server once the worker drains.
 */
import { useQuery } from '@tanstack/react-query';
import { twinService, type EntityListResponse } from '../../services/twinService';
import { OWN_ORG_ENTITY_TYPE, ownOrganisationsQueryBody } from './ownOrganisations';

export const ownOrganisationKeys = {
    all: ['twin', 'entities', OWN_ORG_ENTITY_TYPE, 'own-group'] as const,
};

export function useOwnOrganisations() {
    return useQuery<EntityListResponse>({
        queryKey: ownOrganisationKeys.all,
        queryFn: () => twinService.queryEntities(OWN_ORG_ENTITY_TYPE, ownOrganisationsQueryBody()),
        // Matches every other twin read on the SPA; an edit landing via SSE
        // invalidation still surfaces inside a tick.
        staleTime: 60_000,
    });
}
